import { assetsPerShareAccumulator } from '../accumulator'
import {
  MorphoGeneralPublicResponse,
  GetMarketsResponse,
} from '../../../types/lender/morpho-types'
import { RewardsList } from '../../../types/apiReturnType'
import { apyToApr } from '../../user-data/utils'
import { AdditionalYields } from '../../../types'
import { GenericTokenList } from '../../../types'
import {
  formatNr,
  parseLtv,
  liquidationPenaltyFromLltv,
} from './utils/parsers'
import { zeroAddress } from 'viem'
import { createMarketUid } from '../../../utils'

export function convertMarketsToMorphoResponse(
  response: GetMarketsResponse,
  chainId: string,
  additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const data: { [m: string]: MorphoGeneralPublicResponse } = {}

  const items = response?.markets?.items
  if (!items) return data

  items.forEach((market) => {
    const {
      uniqueKey,
      lltv,
      irmAddress,
      oracleAddress,
      loanAsset,
      collateralAsset,
      state,
      whitelisted,
    } = market
    if (
      collateralAsset &&
      collateralAsset.symbol &&
      loanAsset &&
      loanAsset.symbol &&
      oracleAddress &&
      oracleAddress !== zeroAddress
    ) {
      const m = 'MORPHO_BLUE_' + uniqueKey.slice(2).toUpperCase()
      // @ts-ignore
      if (!data[m]) data[m] = { data: {} }

      // Market-level risk: lltv (0..1) drives the Morpho liquidation
      // incentive. The same penalty applies to the seized collateral, so
      // both config entries below carry it. Morpho permits full
      // liquidation, hence closeFactor = 1.
      const ltv = parseLtv(lltv)
      const liquidationPenalty = liquidationPenaltyFromLltv(ltv)

      // 1. LOAN asset entry
      const loanRewards: RewardsList = []

      state.rewards?.forEach((reward) => {
        loanRewards.push({
          asset: reward.asset.address,
          depositRate: reward.supplyApr * 100,
          variableBorrowRate: reward.borrowApr * 100,
          stableBorrowRate: 0,
        })
      })

      const loanAssetAddress = loanAsset.address.toLowerCase()

      const meta = tokens[loanAssetAddress] ?? loanAsset
      const totalDeposits = formatNr(state.supplyAssets, loanAsset.decimals)
      const totalDebt = formatNr(state.borrowAssets, loanAsset.decimals)
      const utilization = totalDeposits > 0 ? totalDebt / totalDeposits : 0

      const loanMarketUid = createMarketUid(chainId, m, loanAssetAddress)
      data[m].data[loanMarketUid] = {
        marketUid: loanMarketUid,
        name: 'Loan ' + (meta?.symbol ?? loanAsset.symbol ?? ''),
        poolId: loanAssetAddress,
        underlying: loanAssetAddress,
        asset: meta,
        totalDeposits,
        totalDebtStable: 0,
        totalDebt,
        totalDepositsUSD: Number(state.supplyAssetsUsd),
        totalDebtStableUSD: 0,
        totalLiquidity: totalDeposits - totalDebt,
        borrowLiquidity: totalDeposits - totalDebt,
        totalLiquidityUSD:
          Number(state.supplyAssetsUsd) - Number(state.borrowAssetsUsd),
        borrowLiquidityUSD:
          Number(state.supplyAssetsUsd) - Number(state.borrowAssetsUsd),
        totalDebtUSD: Number(state.borrowAssetsUsd),
        utilization,
        accumulator: assetsPerShareAccumulator(
          state.supplyAssets,
          state.supplyShares,
          state.borrowAssets,
          state.borrowShares,
        ),
        depositRate: apyToApr(Number(state.supplyApy)) * 100,
        variableBorrowRate: apyToApr(Number(state.borrowApy)) * 100,
        stableBorrowRate: 0,
        intrinsicYield:
          additionalYields?.intrinsicYields?.[meta?.assetGroup] ?? 0,
        rewards: loanRewards.length > 0 ? loanRewards : undefined,
        decimals: loanAsset.decimals,
        config: {
          0: {
            category: 0,
            borrowCollateralFactor: 0,
            collateralFactor: 0,
            borrowFactor: 1,
            liquidationPenalty,
            closeFactor: 1,
            collateralDisabled: true,
            debtDisabled: false,
          },
        },
        closeFactor: 1,
        collateralActive: false,
        borrowingEnabled: true,
        depositsEnabled: true,
        hasStable: false,
        isActive: true,
        isFrozen: false,
      }

      const collateralAssetAddress = collateralAsset.address.toLowerCase()
      const metaCollateral = tokens[collateralAssetAddress] ?? collateralAsset
      // 2. COLLATERAL asset entry
      const collateralMarketUid = createMarketUid(
        chainId,
        m,
        collateralAssetAddress,
      )
      // A Blue market may name the SAME asset as loan and collateral (degenerate,
      // but it exists — one such market, empty, in a 1,000-market sample). Both
      // rows then key to one uid, and this write lands second: it would replace
      // a borrowable row with a mute collateral one, losing the rate and
      // `borrowingEnabled`. Merge the collateral facts instead of overwriting.
      //
      // Blue has exactly ONE collateral per market and no leg index, so there is
      // nothing to key the two apart by — unlike Midnight, whose legs ARE
      // indexed and therefore get distinct rows (`midnightCollateralRef`).
      const existingLoanRow = data[m].data[collateralMarketUid] as any
      if (existingLoanRow) {
        existingLoanRow.collateralActive = true
        existingLoanRow.config = {
          0: {
            ...(existingLoanRow.config?.[0] ?? {}),
            borrowCollateralFactor: ltv,
            collateralFactor: ltv,
            liquidationPenalty,
            collateralDisabled: false,
          },
        }
      } else {

        data[m].data[collateralMarketUid] = {
          marketUid: collateralMarketUid,
          name:
            'Collateral ' +
            (metaCollateral?.symbol ?? collateralAsset.symbol ?? ''),
          poolId: collateralAssetAddress,
          underlying: collateralAssetAddress,
          asset: metaCollateral,
          totalDeposits: formatNr(
            state.collateralAssets,
            collateralAsset.decimals,
          ),
          totalDebtStable: 0,
          totalDebt: 0,
          totalLiquidity: formatNr(
            state.collateralAssets,
            collateralAsset.decimals,
          ),
          borrowLiquidity: formatNr(
            state.collateralAssets,
            collateralAsset.decimals,
          ),
          totalLiquidityUSD: Number(state.collateralAssetsUsd),
          borrowLiquidityUSD: Number(state.collateralAssetsUsd),
          totalDepositsUSD: Number(state.collateralAssetsUsd),
          totalDebtStableUSD: 0,
          totalDebtUSD: 0,
          utilization: 0,
          depositRate: 0,
          variableBorrowRate: 0,
          stableBorrowRate: 0,
          intrinsicYield:
            additionalYields?.intrinsicYields?.[metaCollateral?.assetGroup] ?? 0,
          rewards: undefined,
          decimals: collateralAsset.decimals,
          config: {
            0: {
              category: 0,
              borrowCollateralFactor: ltv,
              collateralFactor: ltv,
              borrowFactor: 1,
              liquidationPenalty,
              closeFactor: 1,
              collateralDisabled: false,
              debtDisabled: true,
            },
          },
          closeFactor: 1,
          collateralActive: true,
          borrowingEnabled: false,
          depositsEnabled: true,
          hasStable: false,
          isActive: true,
          isFrozen: false,
        }
      }

      data[m].params = {
        market: {
          lender: m,
          collateralDecimals: collateralAsset.decimals,
          loanDecimals: loanAsset.decimals,
          id: uniqueKey,
          lltv,
          oracle: oracleAddress,
          irm: irmAddress,
          collateralAddress: collateralAssetAddress,
          loanAddress: loanAssetAddress,
          isListed: whitelisted,
          fee: state.fee,
          rateAtTarget: state?.rateAtTarget?.toString(),
        },
      }
      data[m].chainId = chainId
    }
  })
  return data
}
